import React, { useEffect, useState } from 'react';

import { Alert, Box, Typography } from '@mui/material';

import { ORDER_STATUS_ABANDONED, ORDER_STATUS_PAYMENT_FAILED } from '~/consts/order';

import { useGetOrderTransactionStatus } from '~/hooks/apis/orders/useGetOrderTransactionStatus';

import { getPayments } from '~/services/PaymentsService';
import { getTimeDifference } from '~/services/TimeService';

import { Payment } from '~/types/services/payment';

import { PaymentLine } from './PaymentLine';

interface Props {
  order: App.Order;
}

type OrderWithStatusLink = App.Order & {
  _links?: {
    status?: {
      href?: string;
    };
  };
};

function SingleOrderPayments({ order }: Props) {
  const [payments, setPayments] = useState<Array<Payment>>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isFailedOrder = order.status === ORDER_STATUS_ABANDONED || order.status === ORDER_STATUS_PAYMENT_FAILED;
  const statusHref = (order as OrderWithStatusLink)._links?.status?.href;
  const { data: transactionStatus } = useGetOrderTransactionStatus(statusHref, isFailedOrder);
  const failureCode = transactionStatus?.errors?.[0]?.code ?? transactionStatus?.code;
  const paymentTypes = transactionStatus?.payment_types ?? [];

  const getRefundTimeDifference = (payment: Payment) => {
    if (!order.created_at || !payment.created_at) {
      return null;
    }

    const orderDate = new Date(order.created_at);
    const refundDate = new Date(payment.created_at);

    // Calculate hours difference to determine if we should include hours/minutes/seconds
    const hoursDiff = Math.abs(refundDate.getTime() - orderDate.getTime()) / (1000 * 60 * 60);
    const includeHoursMinsSecs = hoursDiff < 48;

    return getTimeDifference(refundDate, orderDate, includeHoursMinsSecs, true);
  };

  useEffect(() => {
    const fetchPayments = async () => {
      try {
        const response = await getPayments(order.id_orders);
        setPayments(response.result);
      } catch (err) {
        setError('Failed to load payments');
      } finally {
        setIsLoading(false);
      }
    };

    fetchPayments();
  }, [order.id_orders]);

  if (isLoading) {
    return <div>Loading payments...</div>;
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ mb: 2 }}>
        {error}
      </Alert>
    );
  }

  const sales = payments.filter((payment) => payment.intent === 'sale');
  const refunds = payments.filter((payment) => payment.intent === 'refund');

  return (
    <Box>
      {failureCode && (
        <Box>
          <strong>Failure Code: </strong>
          {failureCode}
        </Box>
      )}
      {paymentTypes.length > 0 && (
        <Box>
          <strong>Payment Methods: </strong>
          {paymentTypes.join(', ')}
        </Box>
      )}
      <Box sx={{ display: 'flex', gap: 4 }}>
        <Box>
          <strong>Payments</strong>
          {sales.length === 0 ? (
            <div>-</div>
          ) : (
            <div>
              {sales.map((payment, index) => (
                <PaymentLine payment={payment} key={index} />
              ))}
            </div>
          )}
        </Box>

        <Box>
          <strong>Refunds</strong>
          {refunds.length === 0 ? (
            <div>-</div>
          ) : (
            <div>
              {refunds.map((payment, index) => {
                const timeDiff = getRefundTimeDifference(payment);
                return (
                  <div key={index}>
                    <PaymentLine payment={payment} />
                    {timeDiff && (
                      <Typography variant="caption" color="text.secondary">
                        Refunded {timeDiff} after order creation
                      </Typography>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </Box>
      </Box>
    </Box>
  );
}

export { SingleOrderPayments };
